import React from 'react';
import { MenuItem } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { AssetIcon } from 'src/assets/icons/AssetIcon';
import { IconResourceUrl } from 'src/assets/icons/ResourceUrl';
import { CollapsingTooltip } from 'src/components/CollapsingTooltip';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import { ResourceThumbnailTooltip } from 'src/components/ResourceThumbnailTooltip';
import { ResourceTypeIcon } from 'src/components/ResourceTypeIcon';
import { PROMPT_MENU_TYPE } from 'src/components/RichSlate';
import { TruncatedTypography } from 'src/components/TruncatedTypography';
import { RESOURCE_CONTENT_TYPE } from 'src/data/performance';

import { useStyles } from './useStyles';

const getWidget = (menuType, quickSearchMode) => {
  if (menuType !== PROMPT_MENU_TYPE.all) {
    return 'add_to_prompt';
  } else {
    if (quickSearchMode) {
      return 'resource_search_quick';
    }
    return 'resource_search';
  }
};

export const PromptMenuItem = ({
  type,
  classes,
  item,
  onUrlClick,
  onImageClick,
  onDocumentClick,
  trackingEvents,
  noTags,
  onBankMessageClick,
  onCloseMenu,
  editor,
  menuType,
  index,
  isApplicationModeInfinity,
  onOpenResourceInNewTab,
  quickSearchMode,
}) => {
  const styles = useStyles({ classes });

  let menuItem;

  switch (type) {
    case PROMPT_MENU_TYPE.image:
      menuItem = (
        <MenuItem
          key={item.id}
          classes={{
            root: cx(styles.listItemSub, styles.listItemSubImage, {
              [styles.listItemSubNoHover]: item.id.startsWith('disabled'),
            }),
          }}
          className={cx(
            { [styles.emptyState]: item.emptyState },
            { [styles.title]: item.title },
            { [styles.createButton]: item.create }
          )}
          onClick={(event) => {
            event.stopPropagation();
            if (item.emptyState || item.title || item.id.startsWith('disabled')) {
              return;
            }

            onImageClick(item);
            trackingEvents?.trackResourceInserted(
              RESOURCE_CONTENT_TYPE.image,
              getWidget(menuType, quickSearchMode)
            );
          }}
        >
          <div className={styles.menuIcon}>
            <div>
              <ResourceThumbnailTooltip
                placement="bottom-start"
                thumbnailUrl={item?.file?.thumbnailUrl}
                onClick={() => onOpenResourceInNewTab(item)}
                label={item.label}
                interactive
              >
                <div
                  className={styles.image}
                  style={{
                    backgroundImage: 'url("' + item?.file?.thumbnailUrl + '")',
                  }}
                ></div>
              </ResourceThumbnailTooltip>
              {item.label && (
                <TruncatedTypography className={cx(styles.label, styles.labelImage)}>
                  {item.label}
                </TruncatedTypography>
              )}
            </div>
          </div>
        </MenuItem>
      );
      break;

    case PROMPT_MENU_TYPE.url:
      menuItem = (
        <MenuItem
          key={item.id}
          classes={{
            root: cx(styles.listItemSub, {
              [styles.listItemSubNoHover]: item.id.startsWith('disabled'),
            }),
          }}
          className={cx(
            { [styles.emptyState]: item.emptyState },
            { [styles.title]: item.title },
            { [styles.createButton]: item.create }
          )}
          onClick={(event) => {
            event.stopPropagation();
            if (!onUrlClick || item.emptyState || item.title || item.id.startsWith('disabled')) {
              return;
            }

            onUrlClick(item.content, noTags);
            trackingEvents?.trackResourceInserted(
              RESOURCE_CONTENT_TYPE.url,
              getWidget(menuType, quickSearchMode)
            );
          }}
        >
          <div className={styles.menuIcon}>
            {!item.title && (
              <div className={styles.iconWrapper}>
                <IconResourceUrl className={styles.assetIcon} />
              </div>
            )}

            <div className={styles.resourceItem}>
              {item.label && (
                <TruncatedTypography className={styles.label}>{item.label}</TruncatedTypography>
              )}
              <CustomTypography
                className={styles.itemContent}
                variant={TYPOGRAPHY_VARIANT.regular12}
              >
                {item.subtitle && <span>{item.subtitle + ' • '}</span>} {item.content}
              </CustomTypography>
            </div>
          </div>
        </MenuItem>
      );
      break;

    case PROMPT_MENU_TYPE.bank:
      menuItem = (
        <MenuItem
          key={`${item.id}-${index}`}
          className={cx(
            { [styles.emptyState]: item.emptyState },
            { [styles.title]: item.title },
            { [styles.createButton]: item.create }
          )}
          classes={{ root: styles.listItemSub }}
          data-testid="messagingBankMenuItem"
          onClick={(event) => {
            event.stopPropagation();
            if (item.emptyState || item.title) {
              return;
            }

            onCloseMenu();
            onBankMessageClick(item, editor, menuType === PROMPT_MENU_TYPE.product);
            trackingEvents?.trackMessageBankInserted(getWidget(menuType, quickSearchMode));
          }}
        >
          <CollapsingTooltip
            title={item.body ? item.label + ' • ' + item.subtitle + ':\n\n' + item.body : ''}
            placement={isApplicationModeInfinity ? 'bottom' : 'right'}
            arrow
            classes={{
              tooltip: styles.messagingBankTooltip,
            }}
            cutoffLength={isApplicationModeInfinity ? 160 : 300}
            enterDelay={500}
          >
            <div className={styles.menuIcon}>
              {!item.title && !item.emptyState && (
                <div className={styles.iconWrapper}>
                  <AssetIcon className={styles.assetIcon} />
                </div>
              )}

              <div className={styles.itemContent}>
                {item.label && (
                  <TruncatedTypography
                    hideTooltip={true}
                    className={styles.label}
                  >
                    {item.label}
                  </TruncatedTypography>
                )}
                {item.subtitle && item.subtitle}{' '}
                {item.brandVoiceName && ' • ' + item.brandVoiceName}
                {item.body && (
                  <TruncatedTypography
                    hideTooltip={true}
                    className={styles.body}
                  >
                    {item.body}
                  </TruncatedTypography>
                )}
              </div>
            </div>
          </CollapsingTooltip>
        </MenuItem>
      );
      break;

    case PROMPT_MENU_TYPE.document:
      menuItem = (
        <MenuItem
          key={`${item.id}-${index}`}
          className={cx(
            { [styles.emptyState]: item.emptyState },
            { [styles.title]: item.title },
            { [styles.createButton]: item.create }
          )}
          classes={{ root: styles.listItemSub }}
          onClick={(event) => {
            event.stopPropagation();
            if (item.emptyState || item.title) {
              return;
            }

            onDocumentClick(item);
            trackingEvents?.trackResourceInserted(
              RESOURCE_CONTENT_TYPE.document,
              getWidget(menuType, quickSearchMode)
            );
          }}
        >
          <ResourceThumbnailTooltip
            placement="right"
            label={item.label}
            onClick={() => onOpenResourceInNewTab(item)}
            thumbnailUrl={item?.file?.thumbnailUrl}
          >
            <div className={styles.menuIcon}>
              {!item.title && !item.emptyState && (
                <div className={styles.iconWrapper}>
                  <ResourceTypeIcon
                    className={styles.assetIcon}
                    type={RESOURCE_CONTENT_TYPE.document}
                    fileType={item.file?.fileType}
                    small
                  />
                </div>
              )}

              <div className={styles.itemContent}>
                {item.label && (
                  <TruncatedTypography
                    hideTooltip={true}
                    className={styles.label}
                  >
                    {item.label}
                  </TruncatedTypography>
                )}
                {item.subtitle && item.subtitle}{' '}
                {item.brandVoiceName && ' • ' + item.brandVoiceName}
                {item.body && (
                  <TruncatedTypography
                    hideTooltip={true}
                    className={styles.body}
                  >
                    {item.body}
                  </TruncatedTypography>
                )}
              </div>
            </div>
          </ResourceThumbnailTooltip>
        </MenuItem>
      );
      break;
    default:
      break;
  }
  return menuItem;
};

PromptMenuItem.propTypes = {
  type: PropTypes.string,
  item: PropTypes.shape(),
  onImageClick: PropTypes.func,
  trackingEvents: PropTypes.shape(),
  noTags: PropTypes.bool,
  onBankMessageClick: PropTypes.func,
  onCloseMenu: PropTypes.func,
  editor: PropTypes.shape(),
  menuType: PropTypes.string,
  index: PropTypes.number,
  isApplicationModeInfinity: PropTypes.bool,
  onDocumentClick: PropTypes.func,
};

PromptMenuItem.defaultProps = {
  type: null,
  item: null,
  onImageClick: null,
  onBankMessageClick: null,
  onCloseMenu: null,
  editor: null,
  onDocumentClick: null,
  trackingEvents: null,
  noTags: false,
  menuType: '',
  index: 0,
  isApplicationModeInfinity: false,
};
